import React from 'react';
import { useToast } from '@chatsdet/ui';
import { CopySimple, Check } from '@phosphor-icons/react';

/**
 * `dark` (default) suits the dark JSON/code blocks this was built for;
 * `surface` uses semantic tokens so the button reads correctly on
 * theme-aware paper surfaces (bg-card/bg-background) in both modes.
 */
export type CopyButtonVariant = 'dark' | 'surface';

const COPY_BUTTON_VARIANT_CLASSES: Record<CopyButtonVariant, string> = {
  dark: 'bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200',
  surface: 'border border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
};

export const CopyButton: React.FC<{
  value: string;
  label?: string;
  variant?: CopyButtonVariant;
  className?: string;
}> = ({ value, label, variant = 'dark', className = '' }) => {
  const { toast } = useToast();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(label ? `Copied ${label}` : 'Copied!');
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`absolute top-2 right-2 z-10 p-1.5 rounded-md ${COPY_BUTTON_VARIANT_CLASSES[variant]} opacity-0 group-hover/json:opacity-100 focus:opacity-100 transition-opacity ${className}`}
      title="Copy"
      aria-label={label ? `Copy ${label}` : 'Copy'}
    >
      {copied ? <Check size={14} /> : <CopySimple size={14} />}
    </button>
  );
};

/**
 * Wraps an existing &lt;pre&gt; (or any block-level child) and overlays
 * a CopyButton that copies `value` to the clipboard. Use this to add
 * copy support to existing JSON/code displays without restyling them.
 */
export const CopyablePre: React.FC<{
  value: string;
  label?: string;
  variant?: CopyButtonVariant;
  children: React.ReactNode;
  className?: string;
}> = ({ value, label, variant, children, className = '' }) => {
  return (
    <div className={`relative group/json ${className}`}>
      <CopyButton value={value} label={label} variant={variant} />
      {children}
    </div>
  );
};
